import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';

export interface MyPendingInvitation {
  id: string;
  workspace_id: string;
  token: string;
  role: string;
  expires_at: string;
  email: string;
  workspace_name: string | null;
}

export function useMyPendingInvitations() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['my-pending-invitations', user?.email],
    enabled: !!user?.email,
    staleTime: 60_000,
    queryFn: async (): Promise<MyPendingInvitation[]> => {
      const userEmail = user?.email?.toLowerCase();
      if (!userEmail) return [];

      const { data, error } = await supabase
        .from('pending_invitations')
        .select('id, workspace_id, token, role, expires_at, email, declined_at, workspaces(name)')
        .ilike('email', userEmail)
        .is('accepted_at', null)
        .is('declined_at', null)
        .gt('expires_at', new Date().toISOString());

      if (error) {
        logger.error('Error fetching pending invitations', error, 'useMyPendingInvitations');
        return [];
      }

      return (data || []).map((row: any) => ({
        id: row.id,
        workspace_id: row.workspace_id,
        token: row.token,
        role: row.role,
        expires_at: row.expires_at,
        email: row.email,
        workspace_name: row.workspaces?.name ?? null,
      }));
    },
  });

  const declineMutation = useMutation({
    mutationFn: async (invitation: { workspace_id: string; token: string }) => {
      const { error } = await supabase
        .from('pending_invitations')
        .update({ declined_at: new Date().toISOString() })
        .eq('workspace_id', invitation.workspace_id)
        .eq('token', invitation.token);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-pending-invitations'] });
    },
  });

  return { ...query, declineInvitation: declineMutation.mutateAsync, isDeclining: declineMutation.isPending };
}
